class FoolFixAnnouncement extends HTMLElement {
  #timer = undefined;
  #listeners = new AbortController();
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  connectedCallback() {
    this.#refreshCutoff();
    this.#showInitial();

    if (this.hasAttribute('data-design-mode')) return;

    const canCycle = !this.#reducedMotion && this.#activeSlides().length > 1;
    const watchCutoff = this.#slides().some((slide) => slide.dataset.cutoffMinutes);

    if (!canCycle && !watchCutoff) return;

    if (canCycle) {
      const { signal } = this.#listeners;

      this.addEventListener('mouseenter', () => this.#pause(), { signal });
      this.addEventListener('mouseleave', () => this.#resume(), { signal });
      this.addEventListener('focusin', () => this.#pause(), { signal });
      this.addEventListener(
        'focusout',
        (event) => {
          if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;

          this.#resume();
        },
        { signal }
      );
      document.addEventListener(
        'visibilitychange',
        () => {
          if (document.hidden) this.#pause();
          else this.#resume();
        },
        { signal }
      );
    }

    this.#play();
  }

  disconnectedCallback() {
    this.#pause();
    this.#listeners.abort();
  }

  #slides() {
    return [...this.querySelectorAll('[data-announcement-slide]')];
  }

  #activeSlides() {
    return this.#slides().filter((slide) => !slide.hidden);
  }

  #bar() {
    return this.closest('.ff-announce');
  }

  #refreshCutoff() {
    if (this.hasAttribute('data-design-mode')) return;

    const renderedAt = Number(this.dataset.renderedAt);
    const renderedMinutes = Number(this.dataset.nowMinutes);

    if (!Number.isFinite(renderedAt) || !Number.isFinite(renderedMinutes)) return;

    const elapsedMinutes = Math.max(0, Math.floor((Date.now() - renderedAt * 1000) / 60000));
    const nowMinutes = (renderedMinutes + elapsedMinutes) % 1440;

    for (const slide of this.#slides()) {
      if (!slide.dataset.cutoffMinutes) continue;

      slide.hidden = nowMinutes >= Number(slide.dataset.cutoffMinutes);
    }
  }

  #showInitial() {
    const active = this.#activeSlides();
    const bar = this.#bar();

    if (active.length === 0) {
      if (bar) bar.hidden = true;
      return;
    }

    if (bar) bar.hidden = false;
    this.#apply(active[0]);
  }

  #apply(current) {
    for (const slide of this.#slides()) {
      const isCurrent = slide === current;

      slide.classList.toggle('ff-announce__slide--active', isCurrent);
      slide.setAttribute('aria-hidden', isCurrent ? 'false' : 'true');
    }
  }

  #advance() {
    this.#refreshCutoff();

    const active = this.#activeSlides();

    if (active.length === 0) {
      const bar = this.#bar();
      if (bar) bar.hidden = true;
      this.#pause();
      return;
    }

    const bar = this.#bar();
    if (bar) bar.hidden = false;

    if (this.#reducedMotion || active.length === 1) {
      this.#apply(active[0]);
      if (!active.some((slide) => slide.dataset.cutoffMinutes)) this.#pause();
      return;
    }

    const current = this.querySelector('.ff-announce__slide--active');
    const currentIndex = active.indexOf(current);
    const next = active[(currentIndex + 1) % active.length];

    this.#apply(next);
  }

  #play() {
    if (this.#timer !== undefined) return;

    const seconds = Number(this.dataset.interval);
    const interval = (Number.isFinite(seconds) && seconds > 0 ? seconds : 5) * 1000;

    this.#timer = window.setInterval(() => this.#advance(), interval);
  }

  #pause() {
    if (this.#timer === undefined) return;

    window.clearInterval(this.#timer);
    this.#timer = undefined;
  }

  #resume() {
    if (document.hidden || this.matches(':hover')) return;
    if (this.contains(document.activeElement)) return;
    if (this.#activeSlides().length < 2) return;

    this.#play();
  }
}

if (!customElements.get('ff-announcement-bar')) {
  customElements.define('ff-announcement-bar', FoolFixAnnouncement);
}
